import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrency, AVAILABLE_CURRENCIES } from "@/contexts/CurrencyContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Settings as SettingsIcon, Palette, Globe, DollarSign, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

export default function Settings() {
  const { currency, setCurrency, formatAmount } = useCurrency();
  const { language, changeLanguage } = useLanguage();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';

  const handleCurrencyChange = (currencyCode: string) => {
    const selectedCurrency = AVAILABLE_CURRENCIES.find(c => c.code === currencyCode);
    if (selectedCurrency) {
      setCurrency(selectedCurrency);
      toast.success(t('settings.currencyChanged', { name: selectedCurrency.name, symbol: selectedCurrency.symbol }));
    }
  };

  const handleLanguageChange = (lang: string) => {
    changeLanguage(lang as 'en' | 'he');
    toast.success(t('settings.languageUpdated'));
  };

  return (
    <div className="page-shell" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="page-heading">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <SettingsIcon className="h-8 w-8 text-primary" />
          {t('settings.title')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Currency Settings */}
      <Card className="p-6 card-gradient border-border/90">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t('settings.currencySettings')}</h2>
          </div>
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">{t('settings.defaultCurrency')}</Label>
              <Select value={currency.code} onValueChange={handleCurrencyChange}>
                <SelectTrigger dir={isRTL ? 'rtl' : 'ltr'}>
                  <SelectValue placeholder={t('settings.selectCurrency')} />
                </SelectTrigger>
                <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
                  {AVAILABLE_CURRENCIES.map((curr) => (
                    <SelectItem key={curr.code} value={curr.code}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{curr.symbol}</span>
                        <span>{curr.name}</span>
                        <span className="text-muted-foreground">({curr.code})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('settings.currentlySelected')}: {currency.name} - {t('settings.example')}: {formatAmount(1234.56)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Language Settings */}
      <Card className="p-6 card-gradient border-border/90">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t('settings.languageRegion')}</h2>
          </div>
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="language">{t('settings.interfaceLanguage')}</Label>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger dir={isRTL ? 'rtl' : 'ltr'}>
                  <SelectValue placeholder={t('settings.selectLanguage')} />
                </SelectTrigger>
                <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      <span>🇺🇸</span>
                      <span>English</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="he">
                    <div className="flex items-center gap-2">
                      <span>🇮🇱</span>
                      <span>עברית (Hebrew)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('settings.languageHelp')}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* App Information */}
      <Card className="p-6 card-gradient border-border/90">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Smartphone className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t('settings.about')}</h2>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('settings.appVersion')}:</span>
              <span className="font-medium">1.0.0</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('settings.build')}:</span>
              <span className="font-medium">{t('settings.production')}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('settings.lastUpdated')}:</span>
              <span className="font-medium">{t('settings.lastUpdatedDate')}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      {/* <Card className="p-6 card-gradient">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Palette className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Quick Actions</h2>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={() => {
              localStorage.clear();
              toast.success('App data cleared successfully');
            }}>
              Clear Local Data
            </Button>
            
            <Button variant="outline" size="sm" onClick={() => {
              window.location.reload();
            }}>
              Refresh App
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Use these actions to troubleshoot or reset your app settings
          </p>
        </div>
      </Card> */}
    </div>
  );
} 